import {
  BarChart2,
  ExternalLink,
  CreditCard,
  Gauge,
  LineChart,
  PieChart,
  List,
  Activity,
  ChartColumnStacked,
  ChartColumnBig,
} from 'lucide-react';

export const WidgetTypeOptions = [
  {
    key: 'bar-chart',
    value: 'Bar Chart',
    icon: BarChart2,
    color: 'text-pink-500',
  },
  {
    key: 'external-link-button',
    value: 'Button',
    icon: ExternalLink,
    color: 'text-green-500',
  },
  {
    key: 'card',
    value: 'Card',
    icon: CreditCard,
    color: 'text-indigo-500',
  },
  {
    key: 'gauge',
    value: 'Gauge',
    icon: Gauge,
    color: 'text-purple-500',
  },
  {
    key: 'line-chart',
    value: 'Line Chart',
    icon: LineChart,
    color: 'text-orange-500',
  },
  {
    key: 'pie-chart',
    value: 'Pie Chart',
    icon: PieChart,
    color: 'text-yellow-500',
  },
  {
    key: 'table-report',
    value: 'Tabular Report',
    icon: List,
    color: 'text-cyan-500',
  },
  {
    key: 'progress-bar',
    value: 'Progress Bar',
    icon: Activity,
    color: 'text-teal-500',
  },
  {
    key: 'stacked-bar',
    value: 'Stacked Bar',
    icon: ChartColumnStacked,
    color: 'text-pink-500',
  },
  {
    key: 'grouped-bar',
    value: 'Grouped Bar',
    icon: ChartColumnBig,
    color: 'text-purple-500',
  },
];
export const WIDGET_FILTER_TYPES = {
  'normal-filter': 'Standard (No Coverage)',
  'user-filter': 'User Coverage',
  'company-filter': 'Company',
  'assigned-to-self': 'Assigned To Technician',
  'user-and-assigned-to-self': 'Coverage & Assigned To Technician',
  'company-and-assigned': 'Company & Assigned To Technician',
  'opportunity-owner': 'Assigned To Opportunity Owner',
};

export const INVENTORY_ADJUSTMENT_PROJECT_TABLE = 'Project';
export const INVENTORY_ADJUSTMENT_TYPE_PROJECT = 'Project.adjustment_type';
export const INVENTORY_ADJUSTMENT_TYPE = 'InventoryAdjustment.adjustment_type';
export const INVENTORY_ADJUSTMENT_PROJECT_OBJECT_ID =
  'Project.adjustment_object_id';
export const INVENTORY_ADJUSTMENT_TABLE = 'InventoryAdjustment';
export const INVENTORY_ADJUSTMENT_OBJECT_ID =
  'InventoryAdjustment.adjustment_object_id';
export const CHARGEBACK_KEY = 'Chargebacks';
export const PROJECT_KEY = 'project';

export const INVENTORY_ADJUSTMENT_TYPES = {
  DISPATCHED: 'Dispatched',
  ALLOCATED: 'Allocated',
  ON_ORDER: 'OnOrder',
};

export const DASHBOARD_REPORT_REDIRECT_CONFIG = {
  LEAD: {
    TABLE_NAME: 'Opportunity',
    ID: 'Opportunity.opportunity_id',
    CUSTOMER_ID: 'Opportunity.customer_primary_key',
  },
};
export const CUBE_LABELS = {
  USER_DETAILS: 'User Details',
  USER_META: 'UserMeta',
};

export const ORDER_BY = [
  { value: 'ASC', name: 'asc' },
  { value: 'DESC', name: 'desc' },
];

export const DASHBOARD_RETRY_LIMIT = 2;

export const onCellEditComplete = e => {
  let { rowData, newValue, field } = e;
  let updatedValue = null;
  Object.keys(e?.rowData)?.forEach(key => {
    const splitKey = key?.split('.');
    if (
      splitKey?.length > 1 &&
      e.newRowData[splitKey[0]] &&
      e.newRowData[splitKey[0]][splitKey[1]]
    ) {
      updatedValue = e?.newRowData[splitKey[0]][splitKey[1]];
    }
  });
  rowData[field] = updatedValue || newValue;
};

export const getFieldType = (selectedValue, masterData, annotation) => {
  if (masterData[selectedValue] && masterData[selectedValue]?.length > 0) {
    return 'dropdown';
  }
  if (annotation && selectedValue && annotation[selectedValue]) {
    const fieldType = annotation[selectedValue]?.type;

    if (fieldType === 'string' && annotation[selectedValue]?.meta?.timeOnly) {
      return 'timeOnly';
    } else if (fieldType === 'string') {
      return 'string';
    } else if (fieldType === 'number') {
      return 'number';
    } else if (
      fieldType === 'time' &&
      annotation[selectedValue]?.meta?.isDateTimeField
    ) {
      return 'dateTime';
    } else if (fieldType === 'time') {
      return 'date';
    }
  }
  return 'string';
};

export const defaultRowsPerPageOptions = [
  10, 25, 50, 100, 500, 1000, 2000, 5000,
];
export const bulkUpdateRowsPerPageOptions = [10, 25, 50, 100, 200, 250];
export const groupByRowsPerPageOptions = [10, 25, 50, 100, 500];
export const removeStatusValues = ['Scheduled', 'Tentatively Scheduled'];
export const filterMasterData = masterData => {
  return {
    ...masterData,
    'Project.status': masterData['Project.status']?.filter(
      item => !removeStatusValues?.includes(item?.value) ?? []
    ),
  };
};

export const getEditableKeys = tableAnnotation => {
  if (!tableAnnotation || typeof tableAnnotation !== 'object') return [];

  return Object.keys(tableAnnotation)?.filter(
    key => tableAnnotation[key]?.meta?.isEditable === true
  );
};

export const headerTitle = (alice, isEditable, isEdit) => {
  return (
    <div className="flex align-items-center gap-2 text-xs">
      {isEditable && isEdit && <i className="pi pi-pencil text-color" />}
      {alice}
    </div>
  );
};

export const hilightCell = (isEditable, isEdit) => {
  return isEditable
    ? 'text-color cursor-pointer'
    : isEdit
      ? 'text-400'
      : 'text-color';
};

export const isUpdateDisabled = (
  selectedCells,
  values,
  filterEditableRecords,
  filterMasterDataList,
  annotation
) => {
  if (!selectedCells?.length) return true;

  return values?.some(field => {
    const filteredValue = filterEditableRecords()?.find(
      item => item?.alice === field?.columnField
    );
    const fieldType = getFieldType(
      filteredValue?.measureName || '',
      filterMasterDataList,
      annotation
    );

    if (!field?.columnField?.trim()) return true;

    switch (fieldType) {
      case 'dropdown':
        return !field?.dropdownField;
      case 'string':
        return !field?.inputField?.trim();
      case 'number':
        return !field?.inputFieldNumber;
      default:
        return false;
    }
  });
};

export const periodComparisonOptions = [
  {
    label: 'Today',
    key: 'today',
  },
  {
    label: 'Yesterday',
    key: 'yesterday',
  },
  {
    label: 'Tomorrow',
    key: 'tomorrow',
  },
  {
    label: 'This week',
    key: 'this Week',
  },
  {
    label: 'This month',
    key: 'this Month',
  },
  {
    label: 'This year',
    key: 'this Year',
  },
  {
    label: 'This Quarter',
    key: 'this Quarter',
  },
  {
    label: 'Last 3 days',
    key: 'last 3 days',
  },
  {
    label: 'Last 7 days',
    key: 'last 7 days',
  },
  {
    label: 'Last 14 days',
    key: 'last 14 days',
  },
  {
    label: 'Last 30 days',
    key: 'last 30 days',
  },
  {
    label: 'Last 365 days',
    key: 'last 365 days',
  },
  {
    label: 'Last week',
    key: 'last week',
  },
  {
    label: 'Last month',
    key: 'last month',
  },
  {
    label: 'Last quarter',
    key: 'last quarter',
  },
];

export const CUBE_WITH_FIELDS_FOR_COMPARISON = [
  {
    cube: 'Project',
    fields: [
      {
        label: 'Created at',
        key: 'Project.created_at',
      },
      {
        label: 'Modified at',
        key: 'Project.modified_at',
      },
    ],
  },
];

export const PERFORMANCE_TYPES = [
  { label: 'Period Comparison', value: 'periodComparison' },
  { label: 'Fixed Target', value: 'fixedTarget' },
];

export const FIELD_COMPARISON_OPERATORS = {
  number: [
    { label: 'Equals', value: 'eq' },
    { label: 'Not Equals', value: 'ne' },
    { label: 'Greater Than', value: 'gt' },
    { label: 'Greater Than or Equal', value: 'gte' },
    { label: 'Less Than', value: 'lt' },
    { label: 'Less Than or Equal', value: 'lte' },
  ],

  date: [
    { label: 'Equals', value: 'eq' },
    { label: 'After', value: 'gt' },
    { label: 'On or After', value: 'gte' },
    { label: 'Before', value: 'lt' },
    { label: 'On or Before', value: 'lte' },
  ],

  time: [
    { label: 'Equals', value: 'eq' },
    { label: 'After', value: 'gt' },
    { label: 'On or After', value: 'gte' },
    { label: 'Before', value: 'lt' },
    { label: 'On or Before', value: 'lte' },
  ],

  string: [
    { label: 'Equals', value: 'eq' },
    { label: 'Not Equals', value: 'ne' },
  ],

  boolean: [{ label: 'Is', value: 'eq' }],
};
